import React from "react";
import { View, TouchableOpacity, Platform } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { LinearGradient } from "expo-linear-gradient";
import { useMedicalHistoryTheme } from "../theme/MedicalHistoryTheme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type SaveButtonProps = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  /** Stable analytics identifier; `label` travels as the event label. */
  action?: string;
};

/**
 * Save button component with gradient border matching the HTML design.
 * Used as the bottom action button in questionnaire screens.
 */
export function SaveButton({
  label,
  onPress,
  disabled = false,
  action,
}: SaveButtonProps) {
  const capturePress = usePressCapture("SaveButton");
  const insets = useSafeAreaInsets();
  const theme = useMedicalHistoryTheme();

  // Account for navbar height (typically 50-60px) + safe area + extra spacing
  const navbarHeight = 70;
  const extraSpacing = 20;
  const bottomOffset =
    navbarHeight + extraSpacing + Math.max(insets.bottom, 16);
  const button = (
    <TouchableOpacity
      onPress={() => {
        capturePress(pressLabel(label), action);
        onPress();
      }}
      disabled={disabled}
      activeOpacity={0.8}
      style={{
        width: "100%",
        maxWidth: 576,
        alignSelf: "center",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <LinearGradient
        colors={theme.completeGradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={{ padding: 1, borderRadius: 12 }}
      >
        <View
          style={{
            width: "100%",
            borderRadius: 11,
            backgroundColor: theme.screenBg,
            paddingVertical: 12,
            paddingHorizontal: 24,
            alignItems: "center",
            justifyContent: "center",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Text
            style={{
              fontSize: 14,
              fontWeight: "600",
              color: theme.text,
            }}
          >
            {label}
          </Text>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );

  if (Platform.OS === "web") {
    return (
      <div
        style={{
          position: "fixed",
          bottom: bottomOffset,
          left: 0,
          right: 0,
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 12,
          backgroundColor: "transparent",
        }}
      >
        {button}
      </div>
    );
  }

  return (
    <View
      style={{
        position: "absolute",
        bottom: bottomOffset,
        left: 0,
        right: 0,
        paddingHorizontal: 24,
        paddingTop: 12,
        backgroundColor: "transparent",
      }}
    >
      {button}
    </View>
  );
}
